import { useTranslation } from 'react-i18next'

import { Text } from '@mercai/clever'

import { formatDateWithTime } from '@/helpers'

import styles from './lead-qualifier-data.module.css'
import {
  extractionFieldMappingOld,
  extractionFieldMappingNew,
  formatExtractionValue,
} from './lead-qualifier-data.constants'
import {
  LeadQualifierDataProps,
  ExtractionFieldProps,
} from './lead-qualifier-data.type'

const ExtractionField = ({
  fieldKey,
  value,
  label,
  icon,
  category,
}: ExtractionFieldProps) => {
  const formattedValue = formatExtractionValue(fieldKey, value)
  const isEmpty = formattedValue === 'N/A'
  const isHighlight =
    !isEmpty &&
    (fieldKey === 'productsUsuallyBought' || fieldKey === 'targetPrice')

  return (
    <div className={styles['table-row']} data-category={category}>
      <div className={styles['category-indicator']} />
      <div className={styles['field-icon']}>{icon}</div>
      <Text variant="text-medium" className={styles['field-label']}>
        {label}
      </Text>
      <Text
        variant="text-medium"
        className={`${styles['field-value']} ${isEmpty ? styles.empty : ''} ${isHighlight ? styles.highlight : ''}`}
      >
        {formattedValue}
      </Text>
    </div>
  )
}

export const LeadQualifierData = ({
  leadQualifier,
}: LeadQualifierDataProps) => {
  const { t } = useTranslation('screenAiCenter')

  const isOldStructure =
    leadQualifier && 'extraction' in leadQualifier && leadQualifier.extraction

  const extraction = isOldStructure
    ? (leadQualifier as { extraction?: unknown })?.extraction
    : (leadQualifier as { checkpoint?: unknown })?.checkpoint
  const qualifiedAt = leadQualifier?.qualifiedAt

  if (!extraction) {
    return (
      <div className={styles['qualifier-section']}>
        <div className={styles['empty-state']}>
          <div className={styles['empty-icon']}>📋</div>
          <Text variant="text-medium">
            {t('conversationShowLeadQualifierNoData')}
          </Text>
        </div>
      </div>
    )
  }

  const fieldMapping = isOldStructure
    ? extractionFieldMappingOld(t)
    : extractionFieldMappingNew(t)

  return (
    <div className={styles['qualifier-section']}>
      {qualifiedAt && (
        <div className={styles['qualifier-meta']}>
          {qualifiedAt && (
            <div className={styles['meta-item']}>
              <Text variant="text-small" className={styles['meta-label']}>
                {t('conversationShowLeadQualifierQualifiedAt')}
              </Text>
              <Text variant="text-small" className={styles['meta-value']}>
                {formatDateWithTime(qualifiedAt, 'es-MX')}
              </Text>
            </div>
          )}
        </div>
      )}

      <div className={styles['extraction-table']}>
        {Object.entries(fieldMapping).map(([fieldKey, fieldConfig]) => {
          const value = extraction[fieldKey as keyof typeof extraction]

          return (
            <ExtractionField
              key={fieldKey}
              fieldKey={fieldKey}
              value={value}
              label={fieldConfig.label}
              icon={fieldConfig.icon}
              category={fieldConfig.category}
            />
          )
        })}
      </div>
    </div>
  )
}
